/**
 * Composer 검색 제안 축(suggestionsFrom)의 web 호스트 어댑터 — rc는 API를 모르고
 * 검색 실행만 호스트에 위임한다(결정 2026-08-05-composer-suggestion-source-axis).
 *
 * 지금 지원하는 카탈로그는 대책 라이브러리 하나뿐이고, 미지원 catalogKey는 **빈 배열**로
 * 응답한다(모르는 키로 엉뚱한 API를 때리지 않는다 — 제안 없이 일반 입력으로 degrade).
 * 필터는 rc의 buildSuggestFilter가 `{ [filterByRowField]: 값 }` 형태로 주므로, 대책
 * 라이브러리가 이해하는 키(category/subCategory)만 골라 검색 파라미터로 넘긴다.
 */
import {
  LAW_ARTICLE_SUGGESTION_SOURCE_KEY,
  MEASURE_SUGGESTION_SOURCE_KEY,
  formatLegalBasisArticleRows,
  formatLegalBasisChip,
  formatLegalBasisDetailBlocks,
  type ComposerSuggestSearch,
  type ComposerSuggestSelect,
  type ComposerSuggestionRow,
  type LegalBasis,
} from '@safetyhub/shared';
import { searchMeasures, incrementMeasureUsage, searchLegalBases, type Measure } from './measures';

/**
 * 카탈로그 키 — 선언 정본은 shared(COMPOSER_SUGGESTION_SOURCES)이고 여기서는 그 키의
 * **조회 구현**만 소유한다. 저작(admin select)과 문자열이 갈라지지 않게 재선언 금지.
 */
export const MEASURE_SUGGEST_CATALOG_KEY = MEASURE_SUGGESTION_SOURCE_KEY;
export const LAW_ARTICLE_SUGGEST_CATALOG_KEY = LAW_ARTICLE_SUGGESTION_SOURCE_KEY;

/**
 * 대책 검색 fetch 상한(raw) — **표시 상한이 아니다.**
 *
 * 서버는 행 단위로 자르고 중복 본문을 합치지 않는데(같은 문장이 여러 지식 항목에 등록돼 있어
 * 한 검색어에 동일 문장 5건이 흔하다), 접기와 기추가 제외는 rc에서 일어난다. 그래서 fetch
 * 상한을 표시 개수에 맞추면 접기 후 두어 건만 남고 제외까지 겹치면 목록이 비어버린다.
 * 임베딩 검색의 raw/cap 분리와 같은 처방 — raw는 넉넉히, 자르기는 rc의
 * `finalizeSuggestionRows`가 접기·제외 **뒤에** 한 번만 한다.
 */
export const MEASURE_SUGGEST_RAW_LIMIT = 40;

/** 빈 문자열/공백만인 필터 값은 필터 없음으로 취급(전체 검색). */
function filterValue(filter: Record<string, string> | undefined, key: string): string | undefined {
  const v = filter?.[key]?.trim();
  return v ? v : undefined;
}

/** 대책 행 → 정규 제안 행. 배지는 분류 체인(구 작성 페이지 제안 목록의 보조 표기와 동형). */
export function measureToSuggestionRow(measure: Measure): ComposerSuggestionRow {
  const chain = measure.subCategory
    ? `${measure.category} > ${measure.subCategory}`
    : measure.category;
  return {
    text: measure.measure,
    badge: chain || undefined,
    // raw는 Measure 그대로 — writes 전파 source 경로와 usage 증가용 id가 여기서 나온다.
    raw: measure as unknown as Record<string, unknown>,
  };
}

/**
 * 대책 라이브러리 검색 — 응답은 분류 그룹 배열이라 평탄화해서 순서 그대로 정규 행으로 만든다.
 * (limit은 서버 기준이라 그룹 합계가 raw 상한을 넘지 않는다. 서버가 usageCount 내림차순으로
 * 스캔해 그룹을 만들므로 그룹 안은 usage 순이다 — 이 순서를 흐트러뜨리지 않아야 rc의 접기가
 * 가장 많이 쓰인 사본을 남긴다.)
 */
export async function searchMeasureSuggestions(
  companyId: string,
  query: string,
  filter?: Record<string, string>,
): Promise<ComposerSuggestionRow[]> {
  const groups = await searchMeasures(companyId, {
    search: query,
    category: filterValue(filter, 'category'),
    subCategory: filterValue(filter, 'subCategory'),
    limit: MEASURE_SUGGEST_RAW_LIMIT,
  });
  return groups.flatMap((group) => group.measures).map(measureToSuggestionRow);
}

/**
 * 법령 조문 fetch 상한(raw) — 대책과 같은 이유로 표시 상한과 분리한다(자르기는 rc).
 * 서버가 이 파라미터를 50으로 clamp하므로 그 위로 올려도 의미가 없다.
 * 조문 블록은 길어 목록이 짧아야 읽히지만, 그 "짧게"는 접기·제외 뒤에 적용돼야 한다.
 */
export const LAW_SUGGEST_RAW_LIMIT = 40;

/**
 * 법령 조문 → 정규 제안 행. 본문 텍스트는 **상세 표시 정본**(`formatLegalBasisDetailBlocks`)이
 * 만든 블록을 그대로 쓴다 — 서버가 조립해 넣는 관련법규 칸과 같은 형식이라, 손으로 고른 조문과
 * 자동으로 실린 조문이 한 목록 안에서 결이 갈리지 않는다.
 */
export function legalBasisToSuggestionRow(item: LegalBasis): ComposerSuggestionRow {
  const [block] = formatLegalBasisDetailBlocks([item]);
  // 조문 단위 소비처(관련법규 표의 법령/조문 두 칼럼)를 위해 **합치기 전 재료**도 raw에 싣는다 —
  // 합본 블록(text)에는 법령명 머리글이 붙어 있어 조문 칼럼에 그대로 넣을 수 없다. 두 값 모두
  // 같은 shared 정본에서 나오므로 서버가 자동으로 실어 준 행과 형식이 같다.
  const [row] = formatLegalBasisArticleRows([item]);
  return {
    text: block ?? [item.article, item.title].filter(Boolean).join(' '),
    badge: item.type || undefined,
    raw: {
      ...(item as unknown as Record<string, unknown>),
      lawName: row?.lawName ?? '',
      articleText: row?.articleText ?? '',
      // 한 줄 식별 표기 — 관련근거를 칩 한 줄로 싣는 칸(위험성평가 결과서 legalBasisText)이 writes로
      // 자기 키에 덮어쓴다. 서버 seed(`formatLegalBasisChip`)와 같은 정본이라 손으로 고른 조문과
      // 자동으로 실린 조문의 형식이 갈리지 않는다(shared rowFields 'chipText' 선언과 짝).
      chipText: formatLegalBasisChip(item),
    },
  };
}

/** 법령 조문 검색 — 조·항·호 통합(서버가 LawArticle+LawSubItem을 합쳐 반환). 필터 없음. */
export async function searchLawArticleSuggestions(
  companyId: string,
  query: string,
): Promise<ComposerSuggestionRow[]> {
  const items = await searchLegalBases(companyId, { search: query, limit: LAW_SUGGEST_RAW_LIMIT });
  return items.map(legalBasisToSuggestionRow);
}

/** 호스트 검색 콜백(rc 계약) — 지원 카탈로그만 실제 조회(미지원 키는 빈 배열 degrade). */
export function createComposerSuggestSearch(companyId: string): ComposerSuggestSearch {
  return async (catalogKey, query, filter) => {
    if (catalogKey === MEASURE_SUGGEST_CATALOG_KEY) {
      return searchMeasureSuggestions(companyId, query, filter);
    }
    if (catalogKey === LAW_ARTICLE_SUGGEST_CATALOG_KEY) {
      return searchLawArticleSuggestions(companyId, query);
    }
    return [];
  };
}

/**
 * 선택 통지 콜백(rc 계약) — 제안 행을 **선택 확정**했을 때만 온다. 대책 라이브러리는 usage를
 * 올린다(fire-and-forget — 실패해도 작성 흐름을 막지 않는다. 구 EditableMeasureList 동형).
 */
export function createComposerSuggestSelect(companyId: string): ComposerSuggestSelect {
  return (catalogKey, row) => {
    if (catalogKey !== MEASURE_SUGGEST_CATALOG_KEY) return;
    const id = row.raw?.id;
    if (typeof id !== 'string' || id === '') return;
    void incrementMeasureUsage(companyId, id).catch(() => {
      // 무시 — 통계 부수효과일 뿐이다.
    });
  };
}
